import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, Plus, CheckSquare, Square } from 'lucide-react';
import { Task, DayData, Resource } from '../types';
import { getWeekNumber, getPhaseForWeek, CATEGORY_COLORS } from '../data/studyPlan';
import './DayPopup.css';

interface DayPopupProps {
  date: Date;
  dayData: DayData;
  onClose: () => void;
  onSave: (data: DayData) => void;
}

export const DayPopup: React.FC<DayPopupProps> = ({ date, dayData, onClose, onSave }) => {
  const [tasks, setTasks] = useState<Task[]>(dayData.tasks);
  const [notes, setNotes] = useState(dayData.notes);
  const [showAddResource, setShowAddResource] = useState<string | null>(null);
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');

  const weekNumber = getWeekNumber(date);
  const phase = getPhaseForWeek(weekNumber);

  useEffect(() => {
    setTasks(dayData.tasks);
    setNotes(dayData.notes);
  }, [dayData]);

  const handleTaskToggle = (taskId: string) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const handleAddResource = (taskId: string) => {
    if (newResourceTitle && newResourceUrl) {
      const newResource: Resource = {
        id: `custom-${Date.now()}`,
        title: newResourceTitle,
        url: newResourceUrl,
        type: 'article',
        isCustom: true
      };
      
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, resources: [...task.resources, newResource] }
          : task
      ));
      
      setNewResourceTitle('');
      setNewResourceUrl('');
      setShowAddResource(null);
    }
  };

  const handleSave = () => {
    const allCompleted = tasks.every(task => task.completed);
    onSave({
      ...dayData,
      tasks,
      notes,
      completed: allCompleted
    });
    onClose();
  };

  const getCategoryLabel = (category: string): string => {
    const labels = {
      'dsa': 'DSA Problems',
      'system-design': 'System Design',
      'cs-fundamentals': 'CS Fundamentals',
      'project': 'Project Work',
      'mock-interview': 'Mock Interview'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const getTaskCount = (category: string): string => {
    const categoryTasks = tasks.filter(t => t.category === category);
    const completed = categoryTasks.filter(t => t.completed).length;
    return `${completed}/${categoryTasks.length}`;
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content" onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <div>
            <h2>{format(date, 'EEEE, MMMM d, yyyy')}</h2>
            <p className="popup-subtitle">Week {weekNumber} - {phase.name} Phase</p>
          </div>
          <button onClick={onClose} className="close-button">
            <X size={20} />
          </button>
        </div>
        
        <div className="popup-body">
          <div className="task-sections">
            {['dsa', 'system-design', 'cs-fundamentals', 'project', 'mock-interview'].map(category => {
              const categoryTasks = tasks.filter(t => t.category === category);
              if (categoryTasks.length === 0) return null;
              
              return (
                <div 
                  key={category} 
                  className="task-section"
                  style={{ backgroundColor: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] }}
                >
                  <h3>{getCategoryLabel(category)} ({getTaskCount(category)})</h3>
                  {categoryTasks.map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-header">
                        <button
                          onClick={() => handleTaskToggle(task.id)}
                          className="task-checkbox"
                        >
                          {task.completed ? <CheckSquare size={20} /> : <Square size={20} />}
                        </button>
                        <span className={task.completed ? 'completed' : ''}>{task.title}</span>
                      </div>
                      
                      <div className="resources">
                        {task.resources.map(resource => (
                          <a
                            key={resource.id}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link"
                          >
                            {resource.title}
                          </a>
                        ))}
                        
                        {showAddResource === task.id ? (
                          <div className="add-resource-form">
                            <input
                              type="text"
                              placeholder="Resource title"
                              value={newResourceTitle}
                              onChange={e => setNewResourceTitle(e.target.value)}
                            />
                            <input
                              type="url"
                              placeholder="URL"
                              value={newResourceUrl}
                              onChange={e => setNewResourceUrl(e.target.value)}
                            />
                            <button onClick={() => handleAddResource(task.id)}>Add</button>
                            <button onClick={() => setShowAddResource(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowAddResource(task.id)}
                            className="add-resource-button"
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          
          <div className="notes-section">
            <h3>Notes</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add your personal notes here..."
              rows={4}
            />
          </div>
        </div>
        
        <div className="popup-footer">
          <button onClick={handleSave} className="save-button">Save</button>
        </div>
      </div>
    </div>
  );
};